import { useTranslation } from 'react-i18next';

import { captureException } from '@sentry/remix';

import useEmail from '~/hooks/useEmail';
import type { ToastData } from '~/hooks/useToast';
import { resolveNectarPageBlock } from '~/utils/nectarPageBlock';
import { getOrCreateAnonymousUserId } from '~/utils/nectarTracking';
import { NECTAR_SITE_ID_FIELD } from '~/utils/nectarTrackingFields';
import toast from '~/utils/toast';

interface Props {
  formId: string;
  shouldConsentToTOS: boolean;
  onError: () => void;
  fetcher: any;
  sendableId?: string;
  sendableType?: string;
  emailFieldName?: string;
  siteId?: string;
}

interface ReturnProps {
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

const useSignupSubmit = ({
  formId,
  shouldConsentToTOS,
  onError,
  fetcher,
  sendableId,
  sendableType,
  emailFieldName = 'email',
  siteId,
}: Props): ReturnProps => {
  const [, setEmail] = useEmail();
  const { t } = useTranslation();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    window._pxFormId = formId;

    // Normalize the SparkLoop-safe field back to `email` (see SignupAction); the
    // empty-email guard below covers a missing value.
    if (emailFieldName !== 'email') {
      formData.set('email', formData.get(emailFieldName) ?? '');
    }

    const tosConsented = formData.get('tos_consent');
    if (shouldConsentToTOS && !tosConsented) {
      toast.error(t('errors.tos_required'));
      onError();
      return;
    }

    const trimmedEmail = (formData.get('email') || '')?.toString().trim();
    // We get a lot of headless bots that submit empty emails. This will remove those from actual submissions and processed sentry errors.
    if (!trimmedEmail) {
      toast.error(t('errors.email_invalid'));
      onError();
      return;
    }

    // Store email in localStorage before submitting so it's available for signup redirects
    setEmail(trimmedEmail);

    formData.set('accepted_tos', String(tosConsented));
    formData.set('email', trimmedEmail);
    if (typeof window !== 'undefined') {
      formData.set('nectar_signup_url', window.location.href);
      formData.set('nectar_anonymous_user_id', getOrCreateAnonymousUserId());
      if (siteId) formData.set(NECTAR_SITE_ID_FIELD, siteId);
      if (sendableId) formData.set('nectar_sendable_id', sendableId);
      if (sendableType) formData.set('nectar_sendable_type', sendableType);

      try {
        // When a submit button has data attribute - `data-nectar-page-block="foo"`
        // We should send that as the nectar_page_block to /create action
        const submitter = e.nativeEvent instanceof SubmitEvent ? e.nativeEvent.submitter : null;
        const pageBlock = resolveNectarPageBlock(submitter);
        if (pageBlock) {
          formData.set('nectar_page_block', pageBlock);
        }
      } catch (e) {
        captureException(e);
      }
    }

    // ActionForm only settles on a toast, so a non-JSON response or network failure would otherwise leave the form loading.
    fetcher
      .submit(formData, {
        method: 'post',
        action: '/create',
      })
      .then(({ data }: { data?: { toast?: ToastData } }) => {
        if (data?.toast) return;

        toast.error(t('errors.default'));
        onError();
      });
  };

  return {
    handleSubmit,
  };
};

export default useSignupSubmit;
